// src/hooks/useProfileData.ts
// Hook untuk mengambil data profil dari Supabase

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Certificate } from '../lib/supabase';
import { fetchMergedCourses } from '../services/courseSyncService';

export interface ProfileData {
  id: string;
  name: string;
  bio: string;
  contact: {
    phone: string;
    email: string;
    address: string;
  };
  profile_image_url: string;
  created_at: string;
  updated_at: string;
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  date: string;
  achievement_date: string;
  category: string;
  level?: string;
  year?: string;
  image_url?: string;
}

export interface Education {
  id: string;
  profile_id?: string;
  school: string;
  level: string;
  start_year: number;
  end_year?: number;
  is_current?: boolean;
  description?: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Tutoring {
  id: string;
  place_name: string;
  subject: string;
  description: string;
  start_date: string;
  end_date?: string;
  is_current: boolean;
  category?: string;
  location?: string;
  status?: string;
}

export interface Gallery {
  id: string;
  profile_id: string;
  title: string;
  description: string;
  image_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CompleteProfileData {
  profile: {
    id: string;
    name: string;
    bio: string;
    contact: any;
    profile_image_url: string;
  };
  achievements: Achievement[];
  education: Education[];
  certificates: Certificate[];
  gallery: Gallery[];
  courses?: any[];
  // Legacy properties for backward compatibility
  fullName: string;
  profileImageUrl: string;
  bio: string;
  educationHistory: Education[];
  certificateHistory: Certificate[];
  courseHistory?: any[];
}

export const useProfileData = () => {
  const [profileData, setProfileData] = useState<CompleteProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .maybeSingle();

      if (profileError) {
        throw new Error(`Error fetching profile: ${profileError.message}`);
      }

      if (!profile) {
        throw new Error('No profile data found');
      }

      // Fetch achievements
      const { data: achievements, error: achievementsError } = await supabase
        .from('achievements')
        .select('*')
        .eq('profile_id', profile.id)
        .order('achievement_date', { ascending: false });

      if (achievementsError) {
        console.warn('Error fetching achievements:', achievementsError.message);
      }

      // Debug: Log achievements data
      console.log('Raw achievements data from database:', achievements);
      if (achievements) {
        console.log('Achievement levels found:', achievements.map(a => a.level));
        const sekolahAchievements = achievements.filter(a => a.level === 'Sekolah');
        console.log('Achievements with level "Sekolah":', sekolahAchievements);
      }

      // Fetch education (temporarily fetch all data since profile_id is null in database)
      const { data: education, error: educationError } = await supabase
        .from('education')
        .select('*')
        .order('start_year', { ascending: false });

      if (educationError) {
        console.warn('Error fetching education:', educationError.message);
      }

      // Fetch certificates (temporarily fetch all data since profile_id is null in database)
      const { data: certificates, error: certificatesError } = await supabase
        .from('certificates')
        .select('*')
        .order('issue_date', { ascending: false });

      if (certificatesError) {
        console.warn('Error fetching certificates:', certificatesError.message);
      }

      // Fetch gallery
      const { data: gallery, error: galleryError } = await supabase
        .from('gallery')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false });

      if (galleryError) {
        console.warn('Error fetching gallery:', galleryError.message);
      }

      // Fetch courses (merged with any synced overrides)
      const courses = await fetchMergedCourses();

      // Transform data to match expected format
      const completeData: CompleteProfileData = {
        profile: {
          id: profile.id,
          name: profile.name,
          bio: profile.bio || '',
          contact: profile.contact || { phone: '', email: '', address: '' },
          profile_image_url: profile.profile_image_url || '/placeholder-profile.jpg'
        },
        achievements: (achievements || []).map(achievement => ({
          ...achievement,
          date: achievement.achievement_date,
          level: achievement.level // Use actual level field from database without fallback
        })),
        education: education || [],
        certificates: certificates || [],
        gallery: gallery || [],
        courses: courses || [],
        // Legacy properties for backward compatibility
        fullName: profile.name,
        profileImageUrl: profile.profile_image_url || '/placeholder-profile.jpg',
        bio: profile.bio || '',
        educationHistory: education || [],
        certificateHistory: certificates || [],
        courseHistory: courses || []
      };

      setProfileData(completeData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  return {
    data: profileData,
    profileData,
    loading,
    error,
    refetch: fetchProfileData
  };
};

// Hook untuk mengambil data profil sederhana (tanpa relasi)
export const useSimpleProfileData = () => {
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .single();

      if (profileError) {
        throw new Error(`Error fetching profile: ${profileError.message}`);
      }

      setProfileData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return {
    profileData,
    loading,
    error,
    refetch: fetchProfile
  };
};